import { ElementRef, Injectable, inject } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { LoggerService } from '@services/utils/logger.service';

/**
 * Marks the widget's OWN CDK overlay container. When the container has to stay on `document.body`
 * (below), the document copy of the partner's custom CSS is scoped under this class so it can style
 * the widget's datepicker/dialog panels and nothing else on the integrator's page - including a
 * host page's own `.cdk-overlay-container`, if the page happens to be an Angular app itself.
 */
export const SMART_WIDGET_OVERLAY_CONTAINER_CLASS = 'cbsw-overlay-container';

/**
 * Keeps the CDK overlay container inside the widget's shadow root instead of `document.body`.
 *
 * Why: ID-reference ARIA attributes don't resolve across a shadow boundary, so the datepicker's
 * `aria-owns` (input -> calendar panel) and MatDialog's `aria-labelledby` silently break for
 * assistive tech. Same-tree restores them. It also puts overlays out of the integrator's CSS reach.
 *
 * Why it can decline: `.cdk-overlay-container` is `position: fixed`, and any ancestor with
 * `transform`/`filter`/`perspective`/`contain`/`will-change` becomes its containing block — which
 * depends on the INTEGRATOR's markup and would misposition or clip the overlay. A visibly broken
 * datepicker beats a missing ARIA relationship, so we leave it on body and warn.
 *
 * Provided by SmartWidgetShadowHostComponent, so iframe mode never sees it.
 */
@Injectable()
export class ShadowRootOverlayContainer extends OverlayContainer {

  private readonly shadowHost = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  protected override _createContainer(): void {
    super._createContainer();

    const shadowRoot = this.shadowHost.shadowRoot;
    if (!shadowRoot || !this._containerElement) {
      return;
    }
    this._containerElement.classList.add(SMART_WIDGET_OVERLAY_CONTAINER_CLASS);
    const blocker = this.fixedPositionContainingBlock();
    if (blocker) {
      LoggerService.warn(
        '[overlay] Leaving the CDK overlay container on document.body: an ancestor of the widget ' +
        'creates a containing block for position:fixed, which would misposition or clip overlays. ' +
        'Datepicker/dialog ARIA id references will not resolve for assistive tech on this page.',
        blocker
      );
      return;
    }
    shadowRoot.appendChild(this._containerElement);
  }

  /** The nearest ancestor of the host that would capture `position: fixed`, or null. */
  private fixedPositionContainingBlock(): Element | null {
    for (let element = this.shadowHost.parentElement; element; element = element.parentElement) {
      const style = getComputedStyle(element);
      if (
        style.transform !== 'none' ||
        style.filter !== 'none' ||
        style.perspective !== 'none' ||
        style.willChange.includes('transform') ||
        style.willChange.includes('filter') ||
        style.willChange.includes('perspective') ||
        /\b(layout|paint|strict|content)\b/.test(style.contain)
      ) {
        return element;
      }
    }
    return null;
  }
}
